/**
 * WebGL engine adapter for browser rendering
 */

import { Engine, type Scene } from "@babylonjs/core";
import type { AnyEngine, EngineOptions } from "../types";
import type { IEngineAdapter } from "./IEngineAdapter";

/**
 * Adapter for standard WebGL Engine
 * Provides browser-based 3D rendering
 */
export class WebGLEngineAdapter implements IEngineAdapter {
  private _engine: Engine | null = null;
  private _canvas: HTMLCanvasElement | null = null;
  private _isInitialized = false;

  /**
   * The underlying Engine instance
   */
  get engine(): AnyEngine | null {
    return this._engine;
  }

  /**
   * The type of engine adapter
   */
  get type(): string {
    return "webgl";
  }

  /**
   * Whether the engine is initialized
   */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Create and initialize the WebGL Engine
   * @param options Engine creation options
   */
  async createEngine(options: EngineOptions): Promise<AnyEngine> {
    if (this._engine) {
      throw new Error("Engine already created");
    }

    if (!options.canvas) {
      throw new Error("Canvas is required for WebGL engine");
    }

    this._canvas = options.canvas;

    this._engine = new Engine(
      this._canvas,
      options.antialias !== false, // Default true
      {
        preserveDrawingBuffer: options.preserveDrawingBuffer || false,
        stencil: options.stencil !== false, // Default true
      },
    );

    // Enable automatic resizing by default
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this._handleResize);
    }

    this._isInitialized = true;

    return this._engine;
  }

  /**
   * Handle window resize events
   */
  private _handleResize = (): void => {
    if (this._engine) {
      this._engine.resize();
    }
  };

  /**
   * Start the render loop
   * @param renderFunction Function to call on each frame
   */
  startRenderLoop(renderFunction: () => void): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    this._engine.runRenderLoop(renderFunction);
  }

  /**
   * Stop the render loop
   */
  stopRenderLoop(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    this._engine.stopRenderLoop();
  }

  /**
   * Resize the engine
   * @param width New width (optional, uses canvas size if not provided)
   * @param height New height (optional, uses canvas size if not provided)
   */
  resize(width?: number, height?: number): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    if (width !== undefined && height !== undefined && this._canvas) {
      this._canvas.width = width;
      this._canvas.height = height;
    }

    this._engine.resize();
  }

  /**
   * Begin a frame
   */
  beginFrame(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }
    this._engine.beginFrame();
  }

  /**
   * End a frame
   */
  endFrame(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }
    this._engine.endFrame();
  }

  /**
   * Render a scene
   * @param scene The scene to render
   */
  renderScene(scene: Scene): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    scene.render();
  }

  /**
   * Dispose of the engine and clean up resources
   */
  dispose(): void {
    // Remove resize listener
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this._handleResize);
    }

    if (this._engine) {
      this._engine.dispose();
      this._engine = null;
    }

    this._canvas = null;
    this._isInitialized = false;
  }

  /**
   * Get the rendering canvas
   */
  getCanvas(): HTMLCanvasElement | null {
    return this._canvas;
  }

  /**
   * Get the current frames per second
   */
  getFps(): number {
    if (!this._engine) {
      return 0;
    }
    return this._engine.getFps();
  }

  /**
   * Get delta time between frames
   */
  getDeltaTime(): number {
    if (!this._engine) {
      return 0;
    }
    return this._engine.getDeltaTime();
  }

  /**
   * Check if the engine supports a feature
   * @param feature Feature name to check
   */
  supportsFeature(feature: string): boolean {
    if (!this._engine) {
      return false;
    }

    switch (feature) {
      case "webgl":
        return true;
      case "webgl2":
        return this._engine.version === 2;
      case "webgpu":
        return false;
      case "webxr":
        return "xr" in navigator;
      case "testing":
      case "nullengine":
        return false;
      default:
        // Check engine capabilities
        return this._engine.getCaps().maxTexturesImageUnits > 0;
    }
  }
}
